import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Menu, X, ChevronDown, ChevronRight, Facebook, Twitter, Linkedin } from 'lucide-react';
import logo from '../assets/logo1.jpeg';
import { getSavedLanguage, setSiteLanguage } from '../i18n/googleTranslate';

const TopBar = styled.div`
  background: var(--primary-blue);
  color: var(--white);
  padding: 0.5rem 0;
  font-size: 0.85rem;
  position: relative;
  z-index: 1001;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const TopBarContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TopBarLeft = styled.div`
  display: flex;
  gap: 2rem;
  opacity: 0.9;
`;

const TopBarRight = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
`;

const SocialLink = styled.a`
  opacity: 0.7;
  transition: var(--transition);

  &:hover {
    opacity: 1;
    color: var(--accent-gold);
  }
`;

const LangSwitcher = styled.select`
  background: rgba(255, 255, 255, 0.15);
  color: var(--white);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.25rem 0.75rem;
  border-radius: var(--radius-sm);
  font-weight: 600;
  cursor: pointer;
  font-size: 0.8rem;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  padding-right: 2rem;

  option {
    background: var(--primary-blue);
    color: var(--white);
  }
`;

const HeaderContainer = styled.header`
  position: fixed;
  top: ${props => props.$scrolled ? '0' : '38px'};
  width: 100%;
  background: ${props => props.$scrolled ? 'rgba(255, 255, 255, 0.98)' : 'var(--white)'};
  backdrop-filter: ${props => props.$scrolled ? 'blur(10px)' : 'none'};
  box-shadow: ${props => props.$scrolled ? 'var(--shadow-lg)' : 'var(--shadow-sm)'};
  z-index: 1000;
  border-bottom: 3px solid var(--accent-gold);
  transition: var(--transition);

  @media (max-width: 1200px) {
    top: 0;
  }
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${props => props.$scrolled ? '75px' : '90px'};
  transition: var(--transition);

  @media (max-width: 1200px) {
    padding: 0 1rem;
    height: 75px;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Logo = styled.img`
  height: ${props => props.$scrolled ? '65px' : '82px'};
  width: auto;
  object-fit: contain;
  transition: var(--transition);

  @media (max-width: 1200px) {
    height: 62px;
  }
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  
  span {
    font-size: 0.7rem;
    color: var(--text-light);
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
  
  strong {
    font-family: var(--font-secondary);
    font-size: 1rem;
    color: var(--primary-blue);
    line-height: 1.2;
  }

  @media (max-width: 1200px) {
    display: none;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 1360px) {
    gap: 0.2rem;
  }

  @media (max-width: 1200px) {
    display: none;
  }
`;

const TopLink = styled(Link)`
  font-weight: 600;
  color: var(--text-dark);
  transition: var(--transition);
  position: relative;
  padding: 0.6rem 1rem;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border-radius: var(--radius-md);

  &:hover {
    color: var(--primary-blue);
    background: var(--background-light);
  }

  &.active {
    color: var(--primary-blue);
    background: rgba(0, 38, 100, 0.05);
  }

  @media (max-width: 1360px) {
    padding: 0.55rem 0.75rem;
    font-size: 0.82rem;
  }
`;

const DropdownRoot = styled.div`
  position: relative;
  padding-bottom: 1rem;
  margin-bottom: -1rem;
`;

const DropdownTrigger = styled.button`
  font-weight: 600;
  color: var(--text-dark);
  transition: var(--transition);
  position: relative;
  padding: 0.6rem 1rem;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border-radius: var(--radius-md);
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    color: var(--primary-blue);
    background: var(--background-light);
  }

  svg {
    transition: var(--transition);
    transform: ${props => props.$open ? 'rotate(180deg)' : 'rotate(0)'};
  }

  @media (max-width: 1360px) {
    padding: 0.55rem 0.75rem;
    font-size: 0.82rem;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% - 0.25rem);
  left: 0;
  min-width: 280px;
  background: var(--white);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  padding: 0.75rem;
  opacity: ${props => (props.$open ? 1 : 0)};
  transform: translateY(${props => (props.$open ? '0' : '10px')}) scale(${props => (props.$open ? '1' : '0.98')});
  pointer-events: ${props => (props.$open ? 'auto' : 'none')};
  transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1001;
  border: 1px solid var(--border-light);

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 2rem;
    width: 12px;
    height: 12px;
    background: var(--white);
    transform: rotate(45deg);
    border-left: 1px solid var(--border-light);
    border-top: 1px solid var(--border-light);
  }
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: var(--radius-md);
  color: var(--text-dark);
  font-weight: 500;
  font-size: 0.9rem;
  transition: var(--transition);

  &:hover {
    background: var(--background-light);
    color: var(--primary-blue);
  }

  svg {
    opacity: 0;
    transition: var(--transition);
  }

  &:hover svg {
    opacity: 1;
    transform: translateX(4px);
  }
`;

const CtaButton = styled(Link)`
  background: var(--secondary-red);
  color: var(--white);
  padding: 0.6rem 1.25rem;
  border-radius: var(--radius-md);
  font-weight: 700;
  font-size: 0.9rem;
  margin-left: 1rem;
  transition: var(--transition);
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: #a50d24;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(200, 16, 46, 0.3);
  }

  @media (max-width: 1360px) {
    margin-left: 0.5rem;
    padding: 0.55rem 0.95rem;
    font-size: 0.82rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  color: var(--primary-blue);
  padding: 0.5rem;
  border-radius: var(--radius-md);
  transition: var(--transition);

  &:hover {
    background: var(--background-light);
  }

  @media (max-width: 1200px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileNav = styled.div`
  position: fixed;
  top: 75px;
  left: 0;
  width: 100%;
  height: calc(100vh - 75px);
  background: var(--white);
  padding: 1.5rem;
  transform: translateX(${props => (props.$isOpen ? '0' : '100%')});
  transition: var(--transition);
  overflow-y: auto;
  display: none;
  z-index: 999;

  @media (max-width: 1200px) {
    display: block;
  }
`;

const MobileGroup = styled.div`
  border-bottom: 1px solid var(--border-light);
  padding: 0.5rem 0;
`;

const MobileTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const MobileMainLink = styled(Link)`
  flex: 1;
  font-weight: 700;
  color: var(--text-dark);
  padding: 1rem 0;
  font-size: 1.1rem;

  &:hover {
    color: var(--primary-blue);
  }
`;

const MobileExpandButton = styled.button`
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  background: var(--background-light);
  color: var(--text-dark);
  transition: var(--transition);

  &:hover {
    background: var(--primary-blue);
    color: var(--white);
  }

  svg {
    transition: var(--transition);
    transform: ${props => props.$open ? 'rotate(90deg)' : 'rotate(0)'};
  }
`;

const MobileSubMenu = styled.div`
  overflow: hidden;
  max-height: ${props => (props.$open ? '500px' : '0')};  
  opacity: ${props => (props.$open ? 1 : 0)};
  transition: all 300ms ease;
  padding-left: 0.5rem;
`;

const MobileSubLink = styled(Link)`
  display: block;
  padding: 0.9rem 1rem;
  margin-bottom: 0.25rem;
  border-radius: var(--radius-md);
  color: var(--text-medium);
  font-weight: 500;
  font-size: 1rem;
  transition: var(--transition);

  &:hover {
    background: var(--background-light);
    color: var(--primary-blue);
  }
`;

const MobileCta = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--secondary-red);
  color: var(--white);
  padding: 1rem;
  border-radius: var(--radius-md);
  font-weight: 700;
  margin-top: 1.5rem;
  font-size: 1rem;
`;

const MobileLangWrapper = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-light);
`;

const MobileLangLabel = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
`;

const MobileLangSelect = styled.select`
  width: 100%;
  padding: 0.75rem 0.9rem;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-dark);
  background: var(--white);
`;

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDesktopMenu, setOpenDesktopMenu] = useState(null);
  const [openMobileSection, setOpenMobileSection] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(getSavedLanguage());
  const location = useLocation();

  const menuItems = useMemo(() => [
    { label: 'Accueil', href: '/' },
    {
      label: 'Institution',
      href: '/institution',
      children: [
        { label: 'Mot du Médiateur', href: '/institution/mot' },
        { label: 'Historique', href: '/institution/historique' },
        { label: 'Rôles et missions', href: '/institution/roles' },
        { label: 'Équipe dirigeante', href: '/institution/equipe' },
      ],
    },
    {
      label: 'Actualités',
      href: '/actualites',
    },
    {
      label: 'Réclamations',
      href: '/services',
      children: [
        { label: 'Déposer une Réclamation', href: '/services/depot-plainte' },
        { label: 'Suivi', href: '/services/suivi' },
        { label: 'Signalement', href: '/services/signalement' },
      ],
    },
    {
      label: 'Documents',
      href: '/documents',
      children: [
        { label: 'Rapports', href: '/documents/rapports' },
        { label: 'Études', href: '/documents/etudes' },
        { label: 'Textes juridiques', href: '/documents/textes-juridiques' },
        { label: 'Communiqués', href: '/actualites/communiques' },
        { label: 'Médias', href: '/actualites/galerie' },
        { label: 'Méthodologie', href: '/services/methodologie' },
      ],
    },
    {
      label: 'Partenaires',
      href: '/partenaires',
      children: [
        { label: 'Institutions partenaires', href: '/partenaires' },
        { label: 'Coopération internationale', href: '/partenaires/cooperation' },
      ],
    },
    {
      label: 'Contact',
      href: '/contact',
    },
  ], []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenMobileSection(null);
  }, [location.pathname]);

  useEffect(() => {
    const onMouseDown = e => {
      const inDropdown = e.target.closest('[data-header-dropdown="true"]');
      if (!inDropdown) setOpenDesktopMenu(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setOpenMobileSection(null);
  };

  const handleLanguageChange = e => {
    const lang = e.target.value;
    setSelectedLanguage(lang);
    setSiteLanguage(lang);
  };

  return (<>
    <TopBar>
      <TopBarContent>
          <TopBarLeft>
            <span>Lun-Ven: 8h00 - 16h00</span>
          </TopBarLeft>
        <TopBarRight>
          <SocialLink href="#" aria-label="Facebook">
            <Facebook size={16} />
          </SocialLink>
          <SocialLink href="#" aria-label="Twitter">
            <Twitter size={16} />
          </SocialLink>
          <SocialLink href="#" aria-label="LinkedIn">
            <Linkedin size={16} />
          </SocialLink>
          <LangSwitcher value={selectedLanguage} onChange={handleLanguageChange}>
            <option value="fr">Français</option>
            <option value="en">English</option>
            <option value="ar">العربية</option>
          </LangSwitcher>
        </TopBarRight>
      </TopBarContent>
    </TopBar>
    <HeaderContainer $scrolled={scrolled}>
      <HeaderContent $scrolled={scrolled}>
        <LogoLink to="/" onClick={closeMobileMenu}>
          <Logo src={logo} alt="Logo" $scrolled={scrolled} />
          <LogoText>
            <span>République du Tchad</span>
            <strong>Médiature de la République</strong>
          </LogoText>
        </LogoLink>

        <Nav aria-label="Navigation principale">
          {menuItems.map(item => {
            const hasChildren = Array.isArray(item.children) && item.children.length > 0;
            const open = openDesktopMenu === item.label;

            if (!hasChildren) {
              return (
                <TopLink 
                  key={item.label} 
                  to={item.href} 
                  onClick={() => setOpenDesktopMenu(null)}
                  className={location.pathname === item.href ? 'active' : ''}
                >
                  {item.label}
                </TopLink>
              );
            }

            return (
              <DropdownRoot 
                key={item.label} 
                data-header-dropdown="true"
                onMouseEnter={() => setOpenDesktopMenu(item.label)}
                onMouseLeave={() => setOpenDesktopMenu(null)}
              >
                <DropdownTrigger 
                  $open={open}
                  onClick={() => setOpenDesktopMenu(open ? null : item.label)}
                >
                  {item.label} <ChevronDown size={16} />
                </DropdownTrigger>

                <DropdownMenu $open={open} role="menu">
                  {item.children.map(child => (
                    <DropdownItem 
                      key={child.href} 
                      to={child.href} 
                      role="menuitem" 
                      onClick={() => setOpenDesktopMenu(null)}
                    >
                      {child.label}
                      <ChevronRight size={16} />
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </DropdownRoot>
            );
          })}

          <CtaButton to="/services/depot-plainte">
            Réclamations
          </CtaButton>
        </Nav>

        <MobileMenuButton onClick={() => setIsMobileMenuOpen(prev => !prev)}>
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </MobileMenuButton>
      </HeaderContent>
    </HeaderContainer>
    <MobileNav $isOpen={isMobileMenuOpen}>
      {menuItems.map(item => {
        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        const isOpen = openMobileSection === item.label;

        return (
          <MobileGroup key={item.label}>
            <MobileTopRow>
              <MobileMainLink to={item.href} onClick={closeMobileMenu}>
                {item.label}
              </MobileMainLink>

              {hasChildren ? (
                <MobileExpandButton 
                  onClick={() => setOpenMobileSection(isOpen ? null : item.label)}
                  $open={isOpen}
                >
                  <ChevronRight size={20} />
                </MobileExpandButton>
              ) : null}
            </MobileTopRow>

            {hasChildren ? (
              <MobileSubMenu $open={isOpen}>
                {item.children.map(child => (
                  <MobileSubLink key={child.href} to={child.href} onClick={closeMobileMenu}>
                    {child.label}
                  </MobileSubLink>
                ))}
              </MobileSubMenu>
            ) : null}
          </MobileGroup>
        );
      })}

      <MobileLangWrapper>
        <MobileLangLabel htmlFor="mobile-language-switcher">Langue</MobileLangLabel>
        <MobileLangSelect
          id="mobile-language-switcher"
          value={selectedLanguage}
          onChange={handleLanguageChange}
        >
          <option value="fr">Français</option>
          <option value="en">English</option>
          <option value="ar">العربية</option>
        </MobileLangSelect>
      </MobileLangWrapper>

      <MobileCta to="/services/depot-plainte" onClick={closeMobileMenu}>
                Déposer une Réclamation
                <ChevronRight size={20} />
      </MobileCta>
    </MobileNav>
  </>);
};

export default Header;
